import { Link, useParams } from "react-router-dom";
import { Headphones, MapPin, Share2, Copy, RefreshCw, Check, Sparkles } from "lucide-react";
import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { CategoryTag } from "@/components/public/Badges";
import { AudioPlayer } from "@/components/public/AudioPlayer";
import { ReadAloud } from "@/components/public/ReadAloud";
import { ArticleCard } from "@/components/public/ArticleCard";
import { SectionHeader } from "@/components/public/SectionHeader";
import { CommentsSection } from "@/components/public/CommentsSection";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useArticleBySlug, useArticles } from "@/hooks/useArticles";
import { useCategories } from "@/hooks/useCategories";
import { usePays } from "@/hooks/usePays";
import { useParametres } from "@/hooks/useParametres";
import { adaptArticle, adaptArticles } from "@/lib/adapters";
import { useSeo, useJsonLd } from "@/hooks/useSeo";
import { SITE_URL, truncate } from "@/lib/seo";
import type { CategorieDto } from "@/types/categorie.types";
import type { PaysDto } from "@/types/pays.types";

function ArticleSkeleton() {
  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-10 md:py-14 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-5 w-20 rounded bg-muted" />
        <div className="h-4 w-16 rounded bg-muted" />
      </div>
      <div className="mt-3 space-y-3">
        <div className="h-8 rounded bg-muted" />
        <div className="h-8 w-3/4 rounded bg-muted" />
      </div>
      <div className="mt-6 flex gap-4">
        <div className="h-3 w-32 rounded bg-muted" />
        <div className="h-3 w-24 rounded bg-muted" />
      </div>
      <div className="mt-8 aspect-[16/9] rounded-md bg-muted" />
      <div className="mt-8 space-y-3">
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 w-2/3 rounded bg-muted" />
      </div>
    </article>
  );
}

// ─── Boutons de partage ───────────────────────────────────────────────────────
function ShareButtons({ title, url }: { title: string; url: string }) {
  const [copied, setCopied] = useState(false);
  const { t } = useI18n();

  const encoded = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const channels = [
    {
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodedTitle}%20${encoded}`,
    },
    {
      label: "Telegram",
      href: `https://t.me/share/url?url=${encoded}&text=${encodedTitle}`,
    },
    {
      label: "X",
      href: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}`,
    },
    {
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback silencieux
    }
  };

  return (
  <div className="mt-6 flex flex-wrap items-center gap-2">
    {channels.map((c) => (
      <a  // ✅ balise manquante
        key={c.label}
        href={c.href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent transition-colors"
      >
        <Share2 className="h-3.5 w-3.5" /> {c.label}
      </a>
    ))}
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent transition-colors"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-verified" /> {t("article.copied")}
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" /> {t("article.copyLink")}
        </>
      )}
    </button>
  </div>
);
}

// ─── Page article ─────────────────────────────────────────────────────────────
function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { locale, t } = useI18n();
  const [coverEnErreur, setCoverEnErreur] = useState(false);
  const [showAiInfo, setShowAiInfo] = useState(false);

  const { data: articleDto, isLoading, isError, refetch } = useArticleBySlug(slug!);
  const { data: categoriesData } = useCategories();
  const { data: paysData } = usePays();
  const { data: parametres } = useParametres();
  const { data: latestData } = useArticles(0, 5, "PUBLIE");

  // Calculées avant les retours anticipés ci-dessous : les hooks de SEO doivent
  // s'exécuter à chaque rendu (règle des Hooks), y compris pendant le chargement.
  const previewArticle = articleDto ? adaptArticle(articleDto) : undefined;
  const previewTitle = previewArticle ? (previewArticle.title[locale] ?? previewArticle.title.fr ?? "") : "";
  const previewCategories: CategorieDto[] = Array.isArray(categoriesData) ? categoriesData : [];
  const previewCat = previewArticle
    ? previewCategories.find((c) => previewArticle.categories?.includes(String(c.id)))
    : undefined;
  const previewCatLabel = previewCat ? (locale === "fr" ? previewCat.libelleFr : previewCat.libelleEn) : undefined;

  useSeo({
    title: previewTitle ? `${previewTitle} — Ramba News` : "Ramba News",
    description: truncate(
      previewArticle?.summary?.[locale] ?? previewArticle?.summary?.fr ??
      previewArticle?.subtitle?.[locale] ?? previewArticle?.subtitle?.fr ??
      previewTitle
    ),
    path: `/article/${slug}`,
    image: previewArticle?.coverImageUrl,
    type: "article",
  });

  useJsonLd("ld-breadcrumb", previewArticle ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: locale === "fr" ? "Accueil" : "Home", item: `${SITE_URL}/` },
      ...(previewCat ? [{ "@type": "ListItem", position: 2, name: previewCatLabel, item: `${SITE_URL}/categories/${previewCat.slug}` }] : []),
      { "@type": "ListItem", position: previewCatLabel ? 3 : 2, name: previewTitle, item: `${SITE_URL}/article/${slug}` },
    ],
  } : undefined);

  if (isLoading) return <ArticleSkeleton />;

  if (isError || !articleDto) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="text-sm text-muted-foreground">{t("error.load")}</p>
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-semibold hover:bg-surface transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" /> {t("cta.retry")}
          </button>
          <Link to="/" className="text-xs text-verified">{t("article.backHome")}</Link>
        </div>
      </div>
    );
  }

  const article = adaptArticle(articleDto);

  // Éviter de répéter dans la galerie le média utilisé comme couverture
  const hasDedicatedCover = !!articleDto.imageCouverture;
  const coverUsedFirstImage = !hasDedicatedCover && !!article.imageUrl;
  const coverUsedFirstVideo = !article.coverImageUrl && !!article.videoUrl;
  const allImages = article.medias?.filter((m) => m.type === "image") ?? [];
  const allVideos = article.medias?.filter((m) => m.type === "video") ?? [];
  const allAudios = article.medias?.filter((m) => m.type === "audio") ?? [];
  const galleryImages = coverUsedFirstImage ? allImages.slice(1) : allImages;
  const galleryVideos = coverUsedFirstVideo ? allVideos.slice(1) : allVideos;
  const hasGallery = galleryImages.length > 0 || galleryVideos.length > 0 || allAudios.length > 0;

  const categories: CategorieDto[] = Array.isArray(categoriesData) ? categoriesData : [];
  const articleCats = categories.filter((c) => article.categories?.includes(String(c.id)));
  const catLabel = articleCats.length
    ? articleCats.map((c) => (locale === "fr" ? c.libelleFr : c.libelleEn)).join(", ")
    : undefined;

  const paysListe: PaysDto[] = Array.isArray(paysData) ? paysData : [];
  const articlePays = paysListe.filter((p) => article.pays?.includes(String(p.id)));
  const paysLabel = articlePays.length
    ? articlePays.map((p) => (locale === "fr" ? p.nom : (p.nomEn || p.nom))).join(", ")
    : undefined;

  const relatedDtos = (Array.isArray(latestData) ? latestData : (latestData as any)?.content ?? [])
    .filter((a: any) => a.slug !== slug)
    .slice(0, 3);
  const related = adaptArticles(relatedDtos);

  const title = article.title[locale] ?? article.title.fr ?? "";
  const subtitle = article.subtitle?.[locale] ?? article.subtitle?.fr;
  const summary = article.summary?.[locale] ?? article.summary?.fr;
  const corps = articleDto.traductions?.find(
    (t) => t.langue?.toLowerCase() === locale
  )?.corps ?? articleDto.traductions?.[0]?.corps;

  // ✅ URL de la page courante pour le partage
  const pageUrl = window.location.href;

  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-10 md:py-14">
      <div className="flex items-center gap-3 text-xs">
        {catLabel && <CategoryTag>{catLabel}</CategoryTag>}
      </div>

      <h1 className="mt-3 font-serif text-3xl md:text-5xl font-semibold leading-[1.1] tracking-tight">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-4 text-lg md:text-xl text-muted-foreground leading-relaxed">{subtitle}</p>
      )}

      {articleDto?.assisteParIa && (
        <button
          type="button"
          onClick={() => setShowAiInfo(true)}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
        >
          <Sparkles className="h-3.5 w-3.5" /> {t("article.aiAssisted.badge")}
        </button>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-border py-4 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">
          {article.byline === "partner-desk" ? t("label.partnerDesk") : t("label.byDesk")}
        </span>
        <span>
          {t("label.published")}{" "}
          {new Date(article.publishedAt).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}
        </span>
        {article.updatedAt && (
          <span>
            {t("label.updated")}{" "}
            {new Date(article.updatedAt).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}
          </span>
        )}
        <span>{article.readingTime} {t("label.readingTime")}</span>
        {paysLabel && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {paysLabel}
          </span>
        )}
      </div>

      <div className="mt-4">
        <ReadAloud title={title} summary={summary} corpsHtml={corps} />
      </div>

      {/* Couverture : imageCouverture > premier média image > vidéo > audio placeholder > dégradé */}
      <div className="media-heavy mt-8 aspect-[16/9] overflow-hidden rounded-md bg-muted">
        {article.coverImageUrl && !coverEnErreur ? (
          <img
            src={article.coverImageUrl}
            alt={(hasDedicatedCover && articleDto.illustration?.texteAlternatif) || title}
            onError={() => setCoverEnErreur(true)}
            className="h-full w-full object-cover"
          />
        ) : article.videoUrl ? (
          <video
            src={article.videoUrl}
            controls
            className="h-full w-full"
          />
        ) : article.audioUrl ? (
          <div className="h-full w-full bg-gradient-to-br from-amber-900/80 to-amber-600/40 flex flex-col items-center justify-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background/90">
              <Headphones className="h-6 w-6 text-foreground" />
            </div>
            <span className="text-sm font-medium text-background/80">{t("label.audioOnly")}</span>
          </div>
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-foreground via-foreground/85 to-verified/40" />
        )}
      </div>

      {/* Légende, crédit et mention « générée par IA » de l'illustration (articles de la newsroom) */}
      {hasDedicatedCover && article.coverImageUrl && !coverEnErreur && articleDto.illustration && (
        <p className="mt-2 text-xs text-muted-foreground">
          {[articleDto.illustration.legende, articleDto.illustration.credit && `© ${articleDto.illustration.credit}`]
            .filter(Boolean)
            .join(" · ")}
          {articleDto.illustration.mentionIa && (
            <span className="ml-2 inline-block rounded bg-muted px-1.5 py-0.5 font-semibold uppercase tracking-wide text-[10px]">
              {articleDto.illustration.mentionIa}
            </span>
          )}
        </p>
      )}

      {/* ✅ Corps de l'article depuis l'API */}
      <div className="prose prose-neutral mt-8 max-w-none text-foreground">
        {summary && (
          <p className="text-lg leading-relaxed font-medium">{summary}</p>
        )}
        {corps && (
          <div
            className="mt-4 text-base leading-relaxed"
            dangerouslySetInnerHTML={{ __html: corps }}
          />
        )}
        {!corps && (
          <>
            <p>{t("article.fallback.p1")}</p>
            <p>{t("article.fallback.p2")}</p>
          </>
        )}
      </div>

      {/* Galerie des médias — médias non utilisés en couverture */}
      {hasGallery && (
        <div className="mt-10 space-y-4">
          {galleryImages.length > 0 && (
            <div className={`grid gap-3 ${
              galleryImages.length >= 3 ? "sm:grid-cols-3" :
              galleryImages.length === 2 ? "sm:grid-cols-2" :
              "grid-cols-1"
            }`}>
              {galleryImages.map((m, i) => (
                <div key={m.s3Key || String(i)} className="overflow-hidden rounded-md aspect-[16/9] bg-muted">
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {galleryVideos.map((m, i) => (
            <div key={m.s3Key || String(i)} className="overflow-hidden rounded-md aspect-[16/9] bg-muted">
              <video src={m.url} controls className="w-full h-full" />
            </div>
          ))}

          {allAudios.map((m, i) => (
            <AudioPlayer
              key={m.s3Key || String(i)}
              title={m.s3Key.split("/").pop() ?? title}
              src={m.url}
            />
          ))}
        </div>
      )}

      {article.isCorrected && (
        <div className="mt-4 rounded-md border border-alert/30 bg-alert/5 p-4 text-sm">
          <div className="font-semibold text-alert">{t("label.correction")}</div>
          <p className="mt-1 text-foreground/80">{t("article.correctionNote")}</p>
        </div>
      )}

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
        {article.tags.length > 0 && (
          <div className="text-xs">
            <div className="font-semibold uppercase tracking-wider text-muted-foreground">
              {t("label.tags")}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="rounded bg-surface px-2 py-1 text-xs text-foreground/80">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}
        {article.availableLanguages.length > 0 && (
          <div className="text-xs">
            <div className="font-semibold uppercase tracking-wider text-muted-foreground">
              {t("label.languages")}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {article.availableLanguages.map((code) => (
                <span
                  key={code}
                  className="rounded border border-border px-2 py-1 text-xs uppercase tracking-wide"
                >
                  {code}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ✅ Partage avec URLs réelles */}
      <ShareButtons title={title} url={pageUrl} />

      {(articleDto?.commentairesAutorises ?? true) && (
        <CommentsSection articleId={articleDto!.id} />
      )}

      {related.length > 0 && (
        <section className="mt-16">
          <SectionHeader title={t("section.related")} />
          <div className="mt-6 grid gap-8 md:grid-cols-3">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} variant="compact" />
            ))}
          </div>
        </section>
      )}

      <Dialog open={showAiInfo} onOpenChange={setShowAiInfo}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{parametres?.["pages.aiCharter.titre"] || t("article.aiAssisted.dialogTitle")}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">
            {parametres?.["pages.aiCharter.corps"] || t("article.aiAssisted.charte")}
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </article>
  );
}

export default ArticlePage;